import { useTranslate } from '../../application/i18n/LocaleProvider';

/**
 * Everything a car's content pack says in words (FR-06).
 *
 * Four bands, in one reading order: what the car IS, why it is a legend, the fun
 * facts, and the design note. That order answers one question at a time — a
 * reader who has just swiped onto the Countach wants to know what it is before
 * they are told why it matters, and the design note is the closing aside rather
 * than the opening claim.
 *
 * THE FUN FACTS ARE THE POINT OF THIS PRODUCT, and they are drawn as such. A
 * bulleted list would file them with the specification, which is exactly what
 * they are not: each one is a small piece of theatre — the F40 having no door
 * handles, Enzo dying thirteen months after presenting it — and they are the
 * thing a reader screenshots and sends somebody. So each is a numbered card with
 * its number set as a display figure, and the three of them read as a rail of
 * three plates rather than as three sentences under a dot.
 *
 * Every band renders nothing when its field is empty. The content contract holds
 * all five fields for all twelve cars (`carProse.test.js` enforces it), so this
 * is not defensive coding against today's pack — it is what lets a thirteenth
 * car ship with two fun facts instead of three without a code change.
 */

/**
 * One band: a heading with the ramp under it, then its paragraph. Local because
 * it is three lines and used three times ten lines away — a component in another
 * file for this would be indirection rather than reuse.
 */
function ProsePanel({ titleKey, children }) {
    const t = useTranslate();

    return (
        <section className="car-prose__panel">
            <header className="section-heading">
                <h2 className="section-heading__text">{t(titleKey)}</h2>
                <span className="section-heading__rule" aria-hidden="true" />
            </header>
            {children}
        </section>
    );
}

export default function CarProse({ car }) {
    const t = useTranslate();
    const prose = car?.prose ?? {};
    const funFacts = prose.funFacts ?? [];

    return (
        <div className="car-prose">
            {prose.description ? (
                <ProsePanel titleKey="cars.section.description">
                    <p className="car-prose__body">{prose.description}</p>
                </ProsePanel>
            ) : null}

            {prose.legend ? (
                <ProsePanel titleKey="cars.section.legend">
                    <p className="car-prose__body">{prose.legend}</p>
                </ProsePanel>
            ) : null}

            {funFacts.length > 0 ? (
                <ProsePanel titleKey="cars.section.funFacts">
                    <ol className="fun-facts">
                        {funFacts.map((fact, position) => (
                            <li className="fun-fact" key={fact}>
                                {/* The ordinal is drawn large, and it is drawn
                                    from the list rather than typed into the
                                    content — `aria-hidden` because the `<ol>`
                                    already numbers these for anyone reading by
                                    ear, and hearing "1, 1." is the pictogram
                                    being counted twice. */}
                                <span className="fun-fact__number figure" aria-hidden="true">
                                    {String(position + 1).padStart(2, '0')}
                                </span>
                                <p className="fun-fact__body">{fact}</p>
                            </li>
                        ))}
                    </ol>
                </ProsePanel>
            ) : null}

            {prose.designNote ? (
                <ProsePanel titleKey="cars.section.designNote">
                    <p className="car-prose__body car-prose__body--note">{prose.designNote}</p>
                </ProsePanel>
            ) : null}
        </div>
    );
}
